import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, Mail, Calendar, User, Sparkles, Paperclip } from 'lucide-react';
import { formatDate, formatDateTime } from '../utils/dateUtils';

const PAGE_SIZE = 50;

interface EmailThread {
  _id: string;
  subject: string;
  participants: string[];
  lastMessageAt: string;
  messageCount: number;
  snippet: string;
  source: 'gmail' | 'outlook';
  isRead: boolean;
  messages: {
    messageId: string;
    from: string;
    to: string[];
    subject: string;
    body: string;
    snippet: string;
    receivedAt: string;
    isRead: boolean;
    attachments?: {
      filename: string;
      mimeType: string;
      size: number;
      attachmentId: string;
    }[];
  }[];
}

interface EmailThreadsModalProps {
  isOpen: boolean;
  onClose: () => void;
  contactId: string;
  contactName: string;
  contactType: 'Client' | 'Supplier' | 'Staff' | 'Contact';
}

export const EmailThreadsModal: React.FC<EmailThreadsModalProps> = ({
  isOpen,
  onClose,
  contactId,
  contactName,
  contactType
}) => {
  const [threads, setThreads] = useState<EmailThread[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedThread, setSelectedThread] = useState<EmailThread | null>(null);
  const [summary, setSummary] = useState<any | null>(null);
  const [summarizing, setSummarizing] = useState(false);

  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const threadsLengthRef = useRef(0);
  useEffect(() => {
    threadsLengthRef.current = threads.length;
  }, [threads]);

  useEffect(() => {
    if (isOpen && contactId) {
      fetchThreads();
    }
  }, [isOpen, contactId]);

  const fetchThreads = async () => {
    try {
      setLoading(true);
      setError(null);
      setTotalCount(0);
      setHasMore(true);
      const token = localStorage.getItem('token');

      const response = await fetch(
        `${apiUrl}/api/email-threads?contactId=${contactId}&contactType=${contactType}&limit=${PAGE_SIZE}&offset=0`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      const data = await response.json();

      if (data.success) {
        setThreads(data.data.threads || []);
        setTotalCount(data.data.pagination?.total ?? (data.data.threads || []).length);
        setHasMore(data.data.pagination?.hasMore ?? false);
      } else {
        setError(data.message || 'Failed to fetch email threads');
      }
    } catch (err) {
      console.error('Fetch threads error:', err);
      setError('Failed to load email threads');
    } finally {
      setLoading(false);
    }
  };

  const loadMoreThreads = useCallback(async () => {
    if (!hasMore || loadingMore) return;
    const offset = threadsLengthRef.current;
    setLoadingMore(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${apiUrl}/api/email-threads?contactId=${contactId}&contactType=${contactType}&limit=${PAGE_SIZE}&offset=${offset}`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      const data = await response.json();
      if (data.success && data.data.threads?.length) {
        setThreads(prev => [...prev, ...data.data.threads]);
        setHasMore(data.data.pagination?.hasMore ?? false);
      } else {
        setHasMore(false);
      }
    } catch (err) {
      console.error('Load more threads error:', err);
      setHasMore(false);
    } finally {
      setLoadingMore(false);
    }
  }, [hasMore, loadingMore, contactId, contactType, apiUrl]);

  const handleScroll = useCallback(() => {
    const el = scrollContainerRef.current;
    if (!el || !hasMore || loadingMore) return;
    const { scrollTop, clientHeight, scrollHeight } = el;
    if (scrollTop + clientHeight >= scrollHeight - 150) {
      loadMoreThreads();
    }
  }, [hasMore, loadingMore, loadMoreThreads]);

  const formatDateRelative = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return formatDate(dateString);
  };

  const handleSummarize = async () => {
    try {
      setSummarizing(true);
      setSummary(null);
      const token = localStorage.getItem('token');

      const response = await fetch(`${apiUrl}/api/email-threads/summarize`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          contactId,
          contactType 
        })
      });

      const data = await response.json();

      if (data.success) {
        setSummary(data.data);
      } else {
        setError(data.message || 'Failed to summarize communications');
      }
    } catch (err) {
      console.error('Summarize error:', err);
      setError('Failed to generate summary');
    } finally {
      setSummarizing(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-600">
          <div className="flex-1">
            <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
              Email Threads - {contactName}
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {contactType} • {totalCount} thread{totalCount !== 1 ? 's' : ''}
            </p>
          </div>
          <div className="flex items-center gap-3">
            {threads.length > 0 && (
              <button
                onClick={handleSummarize}
                disabled={summarizing}
                className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-orange-500 to-pink-500 text-white rounded-lg hover:from-orange-600 hover:to-pink-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-md hover:shadow-lg text-sm"
              >
                <Sparkles className="w-4 h-4" />
                {summarizing ? 'Analyzing...' : 'Ask Buddy to Summarize All'}
              </button>
            )}
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* AI Summary Section */}
        {summary && (
          <div className="border-b border-gray-200 dark:border-gray-600 max-h-[40vh] overflow-y-auto">
            <div className="bg-gradient-to-r from-orange-50 to-pink-50 dark:from-orange-900/20 dark:to-pink-900/20 p-6">
              <div className="flex items-center gap-2 mb-4">
                <Sparkles className="w-6 h-6 text-primary-600 dark:text-primary-400" />
                <h3 className="text-lg font-semibold text-primary-900 dark:text-primary-100">
                  Buddy's Communication Analysis
                </h3>
                <button
                  onClick={() => setSummary(null)}
                  className="ml-auto text-primary-600 hover:text-primary-700 dark:text-primary-400"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="space-y-4 text-sm">
                <div className="grid grid-cols-3 gap-4 pb-4 border-b border-primary-200 dark:border-primary-800">
                  <div>
                    <p className="font-medium text-gray-700 dark:text-gray-300">Threads</p>
                    <p className="text-2xl font-bold text-primary-600 dark:text-primary-400">{summary.threadCount}</p>
                  </div>
                  <div>
                    <p className="font-medium text-gray-700 dark:text-gray-300">Messages</p>
                    <p className="text-2xl font-bold text-primary-600 dark:text-primary-400">{summary.totalMessages}</p>
                  </div>
                  <div>
                    <p className="font-medium text-gray-700 dark:text-gray-300">Participants</p>
                    <p className="text-2xl font-bold text-primary-600 dark:text-primary-400">{summary.participants.length}</p>
                  </div>
                </div>

                <div>
                  <p className="font-medium text-gray-700 dark:text-gray-300 mb-2">Email Thread Topics:</p>
                  <ul className="space-y-1">
                    {summary.threadSummaries.slice(0, 5).map((t: any, i: number) => (
                      <li key={i} className="text-gray-600 dark:text-gray-400 flex items-start gap-2">
                        <span className="text-primary-600 dark:text-primary-400">•</span>
                        <span className="flex-1">{t.subject} ({t.messageCount} messages)</span>
                      </li>
                    ))}
                    {summary.threadSummaries.length > 5 && (
                      <li className="text-gray-500 dark:text-gray-500 italic">
                        ... and {summary.threadSummaries.length - 5} more threads
                      </li>
                    )}
                  </ul>
                </div>

                <div className="pt-4 border-t border-primary-200 dark:border-primary-800">
                  <p className="font-medium text-gray-700 dark:text-gray-300 mb-2">Ready for AI Analysis:</p>
                  <p className="text-gray-600 dark:text-gray-400 italic mb-3">{summary.prompt}</p>
                  <div className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-primary-200 dark:border-primary-800">
                    <p className="text-xs text-gray-500 dark:text-gray-500 mb-2">
                      💡 <strong>How to use:</strong> Copy the full conversation text below and paste it into your AI assistant (ChatGPT, Claude, etc.) along with the prompt above for a comprehensive analysis.
                    </p>
                    <button
                      onClick={() => navigator.clipboard.writeText(summary.conversationText)}
                      className="px-3 py-1 text-xs bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 rounded hover:bg-primary-200 dark:hover:bg-primary-900/50"
                    >
                      📋 Copy All Conversations
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-hidden flex">
          {/* Thread List */}
          <div
            ref={scrollContainerRef}
            onScroll={handleScroll}
            className={`${selectedThread ? 'w-1/3' : 'w-full'} border-r border-gray-200 dark:border-gray-600 overflow-y-auto`}
          >
            {loading ? (
              <div className="flex items-center justify-center p-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
              </div>
            ) : error ? (
              <div className="p-6 text-center">
                <p className="text-error-600 dark:text-error-400">{error}</p>
              </div>
            ) : threads.length === 0 ? (
              <div className="p-8 text-center">
                <Mail className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600 dark:text-gray-400">No email threads found</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-200 dark:divide-gray-600">
                {threads.map((thread) => (
                  <div
                    key={thread._id}
                    onClick={() => setSelectedThread(thread)}
                    className={`p-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${
                      selectedThread?._id === thread._id ? 'bg-primary-50 dark:bg-gray-700 dark:border dark:border-primary-800/50' : ''
                    }`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <h3 className={`text-sm font-semibold ${
                        thread.isRead ? 'text-gray-700 dark:text-gray-300' : 'text-gray-900 dark:text-gray-100'
                      }`}>
                        {thread.subject}
                      </h3>
                      <span className="text-xs text-gray-500 ml-2 flex-shrink-0">
                        {formatDateRelative(thread.lastMessageAt)}
                      </span>
                    </div>
                    <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">
                      {thread.participants.slice(0, 2).join(', ')}
                      {thread.participants.length > 2 && ` +${thread.participants.length - 2}`}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-500 truncate">
                      {thread.snippet}
                    </p>
                    <div className="flex items-center gap-2 mt-2">
                      <span className={`text-xs px-2 py-0.5 rounded ${
                        thread.source === 'gmail' 
                          ? 'bg-error-100 text-error-700 dark:bg-gray-700 dark:border dark:border-error-800/50 dark:text-error-400'
                          : 'bg-accent-100 text-accent-700 dark:bg-gray-700 dark:border dark:border-accent-800/50 dark:text-accent-400'
                      }`}>
                        {thread.source === 'gmail' ? 'Gmail' : 'Outlook'}
                      </span>
                      <span className="text-xs text-gray-500">
                        {thread.messageCount} message{thread.messageCount !== 1 ? 's' : ''}
                      </span>
                    </div>
                  </div>
                ))}
                {loadingMore && (
                  <div className="flex justify-center py-4">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-500" />
                  </div>
                )}
                {!loadingMore && hasMore && threads.length > 0 && threads.length < totalCount && (
                  <div className="py-3 text-center text-xs text-gray-500 dark:text-gray-400">
                    Scroll for more
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Thread Details */}
          {selectedThread && (
            <div className="flex-1 overflow-y-auto p-6">
              <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-4">
                {selectedThread.subject}
              </h3>
              
              <div className="space-y-4">
                {selectedThread.messages.map((message, index) => (
                  <div
                    key={message.messageId}
                    className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 border border-gray-200 dark:border-gray-600"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4 text-gray-500" />
                        <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          {message.from}
                        </span>
                      </div>
                      <div className="flex items-center gap-1 text-xs text-gray-500">
                        <Calendar className="w-3 h-3" />
                        {formatDateTime(message.receivedAt)}
                      </div>
                    </div>
                    
                    <div className="text-xs text-gray-600 dark:text-gray-400 mb-2">
                      To: {message.to.join(', ')}
                    </div>
                    
                    <div className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                      {message.snippet || message.body}
                    </div>
                    
                    {message.attachments && message.attachments.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-600">
                        <div className="flex items-center gap-2 mb-2">
                          <Paperclip className="w-4 h-4 text-gray-500" />
                          <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                            {message.attachments.length} {message.attachments.length === 1 ? 'attachment' : 'attachments'}
                          </span>
                        </div>
                        <div className="space-y-1">
                          {message.attachments.map((attachment, attIndex) => (
                            <div
                              key={attIndex}
                              className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400 bg-white dark:bg-gray-800 rounded px-2 py-1 border border-gray-200 dark:border-gray-600"
                            >
                              <Paperclip className="w-3 h-3 flex-shrink-0" />
                              <span className="flex-1 truncate">{attachment.filename}</span>
                              <span className="text-gray-500 dark:text-gray-500">
                                {attachment.size > 1024 * 1024
                                  ? `${(attachment.size / (1024 * 1024)).toFixed(2)} MB`
                                  : attachment.size > 1024
                                  ? `${(attachment.size / 1024).toFixed(2)} KB`
                                  : `${attachment.size} B`}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

